import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  DownloadSimple, CaretDown, CaretUp, Calculator, Calendar,
  ChartLineUp, Sigma, CheckCircle, Sparkle, PencilSimpleLine,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtMoney = (n, cur = "INR") =>
  n == null ? "—" : `${cur} ${Math.round(Number(n)).toLocaleString("en-IN")}`;
const fmtFullMoney = (n, cur = "INR") =>
  n == null ? "—" : `${cur} ${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTH_LABEL = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const DEFAULT_DECLS = {
  d80ccd_2: 0, family_pension_deduction: 0,
  d80c: 0, d80d_self: 0, d80d_parents: 0, d80ccd_1b: 0,
  d80e: 0, d80g: 0, d80gg: 0, d80tta: 0, d80eea: 0,
  d80ddb: 0, d80dd: 0, d80u: 0, d24b: 0,
  hra_exempt: 0, lta_exempt: 0, other_exempt: 0,
  basic_annual_for_hra: 0, hra_received_annual: 0, rent_paid_annual: 0, is_metro: false,
};

// ---------------------------------------------------------------------------
// Main Payslips page — segmented [Month · YTD · Annual + Tax planner]
// ---------------------------------------------------------------------------
export default function Payslips() {
  const { user } = useAuth();
  const [view, setView] = useState("month");
  const [summary, setSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!user?.employee_id) return;
    api.get("/payslips/mine/summary").then(({ data }) => {
      setSummary(data);
      if (data?.month?.period_month) setSelectedMonth(data.month.period_month);
    }).catch(() => toast.error("Could not load payslip summary"));
  }, [user?.employee_id]);

  const activePayslip = useMemo(() => {
    if (!summary || !selectedMonth) return null;
    return (summary.payslips_in_fy || []).find((p) => p.period_month === selectedMonth) || null;
  }, [summary, selectedMonth]);

  const download = async (p) => {
    if (!p) return;
    setBusy(p.id);
    try {
      const res = await api.get(`/payslips/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `payslip-${p.period_month}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download payslip"); }
    setBusy(null);
  };

  if (!summary) {
    return <div className="border border-zinc-200 bg-white p-8 rounded-sm text-sm text-zinc-500 text-center">Loading your payslip summary…</div>;
  }

  const ytd = summary.ytd || {};
  const ann = summary.annual_projected || {};
  const cur = summary.current_structure?.currency || "INR";

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">My money · FY {summary.fy}</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Payslips</h1>
        <p className="text-sm text-zinc-500 mt-2">
          {summary.months_paid.length} months paid so far this FY · Switch views below.
        </p>
      </div>

      <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-1 text-sm font-medium" data-testid="payslip-view-tabs">
        {[
          { key: "month", label: "For the month", icon: Calendar },
          { key: "ytd", label: "YTD", icon: Sigma },
          { key: "annual", label: "For the year", icon: ChartLineUp },
        ].map(({ key, label, icon: Ic }) => (
          <button key={key} onClick={() => setView(key)} data-testid={`payslip-tab-${key}`}
            className={`px-4 py-1.5 rounded-sm flex items-center gap-1.5 transition-colors ${
              view === key ? "bg-adb-navy text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>
            <Ic size={14} /> {label}
          </button>
        ))}
      </div>

      {view === "month" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Month</Label>
            <select value={selectedMonth || ""}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border border-zinc-200 rounded-sm px-3 py-1.5 text-sm bg-white"
              data-testid="payslip-month-picker">
              {(summary.months_paid || []).slice().reverse().map((m) => (
                <option key={m} value={m}>{MONTH_LABEL(m)}</option>
              ))}
            </select>
            {activePayslip && (
              <Button onClick={() => download(activePayslip)} disabled={busy === activePayslip.id}
                className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm ml-auto" data-testid="payslip-download-current">
                <DownloadSimple size={14} className="mr-1.5" />
                {busy === activePayslip.id ? "Downloading…" : "Download PDF"}
              </Button>
            )}
          </div>
          {activePayslip ? (
            <PayslipDetail p={activePayslip} open={open} setOpen={setOpen} cur={cur} />
          ) : (
            <div className="border border-zinc-200 bg-white p-8 rounded-sm text-sm text-zinc-500 text-center">No payslip issued for this month yet.</div>
          )}
        </div>
      )}

      {view === "ytd" && (
        <div className="border border-zinc-200 bg-white rounded-sm p-6 space-y-4" data-testid="payslip-ytd-view">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-700">Year to date · {summary.fy}</div>
            <div className="font-display text-3xl font-bold mt-1" data-testid="ytd-net-pay">{fmtFullMoney(ytd.net_pay, cur)}</div>
            <div className="text-xs text-zinc-500 mt-1">
              across {ytd.months_count} paid months (April – {MONTH_LABEL(summary.months_paid.slice(-1)[0])})
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm border-t border-zinc-100 pt-4">
            <KV label="Gross earnings" value={fmtMoney(ytd.gross, cur)} />
            <KV label="Basic" value={fmtMoney(ytd.basic, cur)} muted />
            <KV label="HRA" value={fmtMoney(ytd.hra, cur)} muted />
            <KV label="Special allowance" value={fmtMoney(ytd.special_allowance, cur)} muted />
            <KV label="Conveyance" value={fmtMoney(ytd.conveyance, cur)} muted />
            <KV label="Medical" value={fmtMoney(ytd.medical_allowance, cur)} muted />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm border-t border-zinc-100 pt-4">
            <KV label="Employee PF" value={fmtMoney(ytd.employee_pf, cur)} />
            <KV label="Employee ESIC" value={fmtMoney(ytd.employee_esic, cur)} />
            <KV label="Professional Tax" value={fmtMoney(ytd.professional_tax, cur)} />
            <KV label="TDS (deducted)" value={fmtMoney(ytd.tds, cur)} />
            <KV label="Other" value={fmtMoney(ytd.other_deductions, cur)} />
            <KV label="Total deductions" value={fmtMoney(ytd.total_deductions, cur)} strong />
          </div>
        </div>
      )}

      {view === "annual" && (
        <div className="space-y-4">
          <div className="border border-zinc-200 bg-white rounded-sm p-6" data-testid="payslip-annual-view">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-widest text-emerald-700">Projected FY {summary.fy}</div>
                <div className="font-display text-3xl font-bold mt-1" data-testid="annual-net-pay">{fmtFullMoney(ann.net_pay, cur)}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  YTD ({ann.months_paid} months) + forecast for {ann.months_forecasted} remaining months at {fmtMoney(ann.monthly_gross_forecast_basis, cur)}/mo
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Projected gross</div>
                <div className="text-xl font-bold" data-testid="annual-gross">{fmtFullMoney(ann.gross, cur)}</div>
                <div className="text-xs text-zinc-500 mt-1">Deductions {fmtMoney(ann.total_deductions, cur)}</div>
              </div>
            </div>
          </div>
          <TaxPanel summary={summary} cur={cur} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tax planner — declarations form + compare + election
// ---------------------------------------------------------------------------
function TaxPanel({ summary, cur }) {
  const [decls, setDecls] = useState(DEFAULT_DECLS);
  const [compareResult, setCompareResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [election, setElection] = useState(null);
  const [electing, setElecting] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api.get("/payslips/mine/regime-election").then(({ data }) => {
      if (data) {
        setElection(data);
        if (data.declarations) setDecls({ ...DEFAULT_DECLS, ...data.declarations });
      }
    }).catch(() => {});
  }, [summary.fy]);

  const setD = (key, value) => setDecls((prev) => ({
    ...prev, [key]: typeof value === "boolean" ? value : Number(value || 0),
  }));

  const runCompare = async () => {
    setBusy(true);
    setCompareResult(null);
    try {
      const { data } = await api.post("/payslips/mine/tax-compare", { declarations: decls });
      setCompareResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const elect = async (regime) => {
    setElecting(true);
    try {
      await api.post("/payslips/mine/regime-election", { regime, declarations: decls });
      const { data } = await api.get("/payslips/mine/regime-election");
      setElection(data);
      setEditing(false);
      setCompareResult(null);
      toast.success(`Elected ${regime.toUpperCase()} regime for FY ${summary.fy}. Payroll will deduct TDS accordingly.`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setElecting(false);
  };

  const showForm = !election || editing;

  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid="tax-panel">
      {/* Header */}
      <div className="p-6 border-b border-zinc-100">
        <div className="flex items-center gap-2 flex-wrap">
          <Calculator size={18} className="text-emerald-700" />
          <h2 className="font-display text-2xl font-bold">Income Tax planner</h2>
          <Badge variant="outline" className="ml-auto rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]" data-testid="tax-statute-badge">
            FY {summary.fy} · Income-tax Act, 2025 (FA 2026)
          </Badge>
        </div>
        <p className="text-sm text-zinc-500 mt-2">
          Declare your investments &amp; exemptions, compare tax under both regimes,
          then elect the one you want payroll to use for monthly TDS.
        </p>
      </div>

      {/* Election status card (when locked) */}
      {election && !editing && (
        <div className="p-5 bg-emerald-50/50 border-b border-emerald-200" data-testid="election-status">
          <div className="flex items-start gap-3 flex-wrap">
            <CheckCircle size={20} weight="fill" className="text-emerald-600 flex-none mt-0.5" />
            <div className="flex-1 min-w-[220px]">
              <div className="font-medium text-emerald-900">
                You elected <span className="uppercase">{election.regime}</span> regime for FY {election.fy}
              </div>
              <div className="text-xs text-emerald-800 mt-1">
                Confirmed on {new Date(election.elected_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                {" · "}Payroll will deduct TDS as per this regime.
              </div>
            </div>
            <Button onClick={() => setEditing(true)} variant="outline" size="sm" className="rounded-sm"
              data-testid="change-election-btn">
              <PencilSimpleLine size={12} className="mr-1.5" /> Update declarations
            </Button>
          </div>
        </div>
      )}

      {/* Declarations form */}
      {showForm && (
        <>
          <DeclarationsForm decls={decls} setD={setD} cur={cur} />
          <div className="p-6 flex items-center gap-3 flex-wrap border-t border-zinc-100 bg-zinc-50/50">
            <Button onClick={runCompare} disabled={busy}
              className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="compare-regimes-btn">
              <Calculator size={14} className="mr-1.5" />
              {busy ? "Comparing…" : "Compare both regimes"}
            </Button>
            <div className="text-xs text-zinc-500">
              Basis: projected annual gross of <span className="font-medium">{fmtMoney(summary.annual_projected.gross, cur)}</span>
            </div>
          </div>
        </>
      )}

      {/* Compare result */}
      {compareResult && showForm && (
        <div className="border-t border-zinc-100 p-6 space-y-4 bg-gradient-to-b from-emerald-50/30 to-white" data-testid="compare-result">
          <RecommendationBanner cmp={compareResult} cur={cur} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RegimeResultCard r={compareResult.new_regime} cur={cur}
              recommended={compareResult.recommendation === "new"}
              onElect={() => elect("new")} electing={electing} />
            <RegimeResultCard r={compareResult.old_regime} cur={cur}
              recommended={compareResult.recommendation === "old"}
              onElect={() => elect("old")} electing={electing} />
          </div>
          <div className="text-xs text-zinc-500 space-y-1 border-t border-zinc-100 pt-3">
            <div>• Confirm your regime so payroll can deduct the correct monthly TDS.</div>
            <div>• You can change your election any time before Form 16 issuance in June {parseInt(summary.fy.split("-")[0]) + 1}.</div>
            <div>• Deduction caps are auto-enforced. HRA is auto-computed when Basic + HRA + Rent are provided.</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Declarations form — grouped sections with cap hints
// ---------------------------------------------------------------------------
function DeclarationsForm({ decls, setD, cur }) {
  const autoHRA = useMemo(() => {
    const { basic_annual_for_hra: b, hra_received_annual: h, rent_paid_annual: r, is_metro } = decls;
    if (!b || !h || !r) return 0;
    const a = h;
    const c = is_metro ? 0.5 * b : 0.4 * b;
    const rentPart = Math.max(0, r - 0.10 * b);
    return Math.round(Math.max(0, Math.min(a, c, rentPart)));
  }, [decls]);

  return (
    <div className="p-6 space-y-4" data-testid="declarations-form">
      <Section title="Allowed in BOTH regimes" testid="section-both" tone="emerald" defaultOpen>
        <DedInput label="80CCD(2) — Employer's NPS contribution"
          hint="Employer share only · Up to 10% of Basic (14% govt) · Ask HR for the amount"
          testid="input-80ccd_2"
          value={decls.d80ccd_2} onChange={(v) => setD("d80ccd_2", v)} />
        <DedInput label="Family pension deduction"
          hint="₹25k (new regime) or ₹15k (old) · Whichever less of that or 1/3rd of pension"
          testid="input-family_pension"
          value={decls.family_pension_deduction} onChange={(v) => setD("family_pension_deduction", v)} />
      </Section>

      <Section title="Old regime only — Chapter VI-A deductions"
        subtitle="Not available under the new regime"
        testid="section-chvia" tone="amber">
        <DedInput label="80C — LIC · PPF · ELSS · EPF · Home Loan Principal · Tuition"
          hint="Cap ₹1,50,000" testid="input-80c"
          value={decls.d80c} onChange={(v) => setD("d80c", v)} />
        <DedInput label="80CCD(1B) — NPS Tier-I additional"
          hint="Cap ₹50,000 · Over and above 80C" testid="input-80ccd_1b"
          value={decls.d80ccd_1b} onChange={(v) => setD("d80ccd_1b", v)} />
        <DedInput label="80D — Self & family health insurance premium"
          hint="Cap ₹25,000 · ₹50,000 if self is senior citizen" testid="input-80d_self"
          value={decls.d80d_self} onChange={(v) => setD("d80d_self", v)} />
        <DedInput label="80D — Parents health insurance premium"
          hint="Cap ₹25,000 · ₹50,000 if parents are senior citizens" testid="input-80d_parents"
          value={decls.d80d_parents} onChange={(v) => setD("d80d_parents", v)} />
        <DedInput label="80E — Education loan interest"
          hint="No monetary cap · Deductible for 8 years" testid="input-80e"
          value={decls.d80e} onChange={(v) => setD("d80e", v)} />
        <DedInput label="80G — Donations (qualifying amount)"
          hint="50% or 100% eligible depending on charity" testid="input-80g"
          value={decls.d80g} onChange={(v) => setD("d80g", v)} />
        <DedInput label="80GG — Rent paid (only if no HRA received)"
          hint="Cap ₹60,000 · Least of ₹5k/mo, 25% income, or (rent - 10% income)"
          testid="input-80gg"
          value={decls.d80gg} onChange={(v) => setD("d80gg", v)} />
        <DedInput label="80TTA / 80TTB — Savings bank interest"
          hint="Cap ₹10,000 · ₹50,000 for senior citizens under 80TTB" testid="input-80tta"
          value={decls.d80tta} onChange={(v) => setD("d80tta", v)} />
        <DedInput label="80EEA — First home-loan additional interest"
          hint="Cap ₹1,50,000 · Loan sanctioned before 31 Mar 2022"
          testid="input-80eea"
          value={decls.d80eea} onChange={(v) => setD("d80eea", v)} />
        <DedInput label="80DDB — Specified diseases treatment"
          hint="Cap ₹1,00,000 (senior) · ₹40,000 otherwise" testid="input-80ddb"
          value={decls.d80ddb} onChange={(v) => setD("d80ddb", v)} />
        <DedInput label="80DD — Disabled dependant maintenance"
          hint="Cap ₹1,25,000 · Certificate required" testid="input-80dd"
          value={decls.d80dd} onChange={(v) => setD("d80dd", v)} />
        <DedInput label="80U — Self disability"
          hint="Cap ₹1,25,000 · Certificate required" testid="input-80u"
          value={decls.d80u} onChange={(v) => setD("d80u", v)} />
      </Section>

      <Section title="Old regime only — Salary exemptions & home loan"
        subtitle="Not available under the new regime"
        testid="section-exemptions" tone="amber">
        {/* HRA helper */}
        <div className="col-span-full border border-zinc-200 rounded-sm p-4 bg-emerald-50/30 space-y-3" data-testid="hra-helper">
          <div className="flex items-center gap-2">
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-medium">HRA exemption helper · u/s 10(13A)</div>
            <div className="text-[10px] text-emerald-800 ml-auto">Auto-computes the least-of formula</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DedInput label="Annual Basic (₹)" testid="input-hra-basic"
              value={decls.basic_annual_for_hra} onChange={(v) => setD("basic_annual_for_hra", v)} />
            <DedInput label="Annual HRA received (₹)" testid="input-hra-received"
              value={decls.hra_received_annual} onChange={(v) => setD("hra_received_annual", v)} />
            <DedInput label="Annual rent paid (₹)" testid="input-hra-rent"
              value={decls.rent_paid_annual} onChange={(v) => setD("rent_paid_annual", v)} />
            <label className="flex items-center gap-2 cursor-pointer pt-6">
              <input type="checkbox" checked={decls.is_metro}
                onChange={(e) => setD("is_metro", e.target.checked)}
                data-testid="input-hra-metro"
                className="w-4 h-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500" />
              <span className="text-xs">Metro city (Mumbai / Delhi / Chennai / Kolkata)</span>
            </label>
          </div>
          <div className="flex items-center justify-between text-sm border-t border-emerald-200 pt-2">
            <div className="text-[11px] text-zinc-600">
              Formula: LEAST of · HRA received · {decls.is_metro ? "50%" : "40%"} of Basic · (Rent − 10% of Basic)
            </div>
            <div className="font-mono-plex font-semibold text-emerald-800" data-testid="hra-auto-computed">
              Exempt: {fmtMoney(autoHRA, cur)}
            </div>
          </div>
          <div className="text-[10px] text-zinc-500">
            Leave the "HRA exempt (manual override)" field below empty to use this auto-computed value.
          </div>
        </div>
        <DedInput label="HRA exempt (manual override)"
          hint="Leave 0 to use the auto-computed value above" testid="input-hra-manual"
          value={decls.hra_exempt} onChange={(v) => setD("hra_exempt", v)} />
        <DedInput label="LTA exemption u/s 10(5)"
          hint="Actual amount · Twice in a 4-year block · Travel within India only"
          testid="input-lta"
          value={decls.lta_exempt} onChange={(v) => setD("lta_exempt", v)} />
        <DedInput label="24(b) — Home loan interest (self-occupied)"
          hint="Cap ₹2,00,000 · Let-out property has no cap" testid="input-24b"
          value={decls.d24b} onChange={(v) => setD("d24b", v)} />
        <DedInput label="Other exemptions (children edu, uniform, etc.)"
          hint="Total of remaining §10 exemptions you're claiming"
          testid="input-other"
          value={decls.other_exempt} onChange={(v) => setD("other_exempt", v)} />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentational sub-components
// ---------------------------------------------------------------------------
function Section({ title, subtitle, tone = "emerald", testid, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const toneClass = tone === "amber" ? "text-amber-800 bg-amber-50" : "text-emerald-700 bg-emerald-50";
  return (
    <div data-testid={testid} className="border border-zinc-200 rounded-sm overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-2 p-3 text-left hover:brightness-95 transition ${toneClass}`}
        data-testid={`${testid}-toggle`}>
        <div className="text-xs uppercase tracking-widest font-medium">{title}</div>
        {subtitle && <div className="text-[11px] opacity-80">· {subtitle}</div>}
        <div className="ml-auto">{open ? <CaretUp size={14} /> : <CaretDown size={14} />}</div>
      </button>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white">{children}</div>
      )}
    </div>
  );
}

function DedInput({ label, hint, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-zinc-700">{label}</Label>
      <Input type="number" min={0} step={1000} value={value || 0}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-sm" data-testid={testid} />
      {hint && <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed">{hint}</div>}
    </div>
  );
}

function RecommendationBanner({ cmp, cur }) {
  const { recommendation, annual_savings_vs_alternative: savings, new_regime, old_regime } = cmp;
  if (recommendation === "either") {
    return (
      <div className="border border-zinc-200 bg-white rounded-sm p-4 text-sm text-center text-zinc-700" data-testid="recommendation-banner">
        Both regimes work out the same for you at this projected income — pick either.
      </div>
    );
  }
  const winner = recommendation === "new" ? "NEW" : "OLD";
  return (
    <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-4 flex items-start gap-3" data-testid="recommendation-banner">
      <Sparkle size={22} weight="fill" className="text-emerald-600 flex-none mt-0.5" />
      <div className="flex-1">
        <div className="font-display text-lg font-bold text-emerald-900">
          Choose the <span>{winner}</span> regime — save <span className="font-mono-plex">{fmtFullMoney(savings, cur)}</span> per year
        </div>
        <div className="text-xs text-emerald-800 mt-1">
          New regime tax {fmtMoney(new_regime.total_annual_tax, cur)} · Old regime tax {fmtMoney(old_regime.total_annual_tax, cur)} · on projected gross of {fmtMoney(cmp.gross_annual, cur)}
        </div>
      </div>
    </div>
  );
}

function RegimeResultCard({ r, recommended, onElect, electing, cur }) {
  const [showSlabs, setShowSlabs] = useState(false);
  return (
    <div className={`border rounded-sm p-4 space-y-3 ${recommended ? "border-emerald-400 bg-white shadow-sm ring-2 ring-emerald-300" : "border-zinc-200 bg-white"}`}
      data-testid={`regime-card-${r.regime}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium">{r.regime} regime</div>
        {recommended && <Badge className="rounded-sm bg-emerald-600 hover:bg-emerald-600 text-white text-[10px]">Recommended</Badge>}
      </div>
      <div>
        <div className="font-display text-2xl font-bold" data-testid={`regime-${r.regime}-total`}>{fmtFullMoney(r.total_annual_tax, cur)}</div>
        <div className="text-xs text-zinc-500">annual tax liability</div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs pt-2 border-t border-zinc-100">
        <KVSmall k="Monthly TDS" v={fmtMoney(r.monthly_tds, cur)} />
        <KVSmall k="Taxable income" v={fmtMoney(r.taxable_income, cur)} />
        <KVSmall k="Deductions used" v={fmtMoney(r.total_deductions, cur)} />
        <KVSmall k="87A rebate" v={fmtMoney(r.rebate_87a, cur)} />
      </div>
      <button onClick={() => setShowSlabs(!showSlabs)}
        className="w-full text-[11px] text-zinc-500 flex items-center justify-center gap-1 py-1 border-t border-zinc-100 hover:text-emerald-700 transition"
        data-testid={`regime-${r.regime}-toggle-slabs`}>
        {showSlabs ? "Hide" : "Show"} slab breakdown {showSlabs ? <CaretUp size={10} /> : <CaretDown size={10} />}
      </button>
      {showSlabs && (
        <div className="text-xs font-mono-plex space-y-1">
          {(r.slab_breakdown || []).map((s, i) => (
            <div key={i} className="flex justify-between text-zinc-600">
              <span>{s.range} @ {s.rate_percent}%</span>
              <span>{fmtMoney(s.tax, cur)}</span>
            </div>
          ))}
          <div className="border-t border-zinc-200 pt-1 flex justify-between font-medium text-zinc-900">
            <span>Tax before rebate</span><span>{fmtMoney(r.tax_before_rebate, cur)}</span>
          </div>
          {r.rebate_87a > 0 && (
            <div className="flex justify-between text-emerald-700"><span>− 87A rebate</span><span>{fmtMoney(r.rebate_87a, cur)}</span></div>
          )}
          {r.marginal_relief > 0 && (
            <div className="flex justify-between text-emerald-700"><span>− Marginal relief</span><span>{fmtMoney(r.marginal_relief, cur)}</span></div>
          )}
          <div className="flex justify-between text-zinc-700"><span>+ Cess 4%</span><span>{fmtMoney(r.cess_4pct, cur)}</span></div>
          <div className="border-t border-zinc-200 pt-1 flex justify-between font-semibold text-zinc-900">
            <span>Total</span><span>{fmtMoney(r.total_annual_tax, cur)}</span>
          </div>
        </div>
      )}
      <Button onClick={onElect} disabled={electing}
        className={`w-full rounded-sm ${recommended ? "btn-zenith text-white" : "bg-adb-navy hover:bg-adb-navy-800"}`}
        data-testid={`elect-${r.regime}-btn`}>
        <CheckCircle size={14} className="mr-1.5" />
        {electing ? "Saving…" : `Elect ${r.regime.toUpperCase()} regime`}
      </Button>
    </div>
  );
}

function PayslipDetail({ p, open, setOpen, cur }) {
  const snap = p.structure_snapshot || {};
  const customE = snap.custom_earnings || {};
  const customD = snap.custom_deductions || {};
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-5 space-y-3" data-testid={`payslip-card-${p.period_month}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">{MONTH_LABEL(p.period_month)}</div>
            <div className="font-display text-3xl font-bold mt-1">{fmtFullMoney(p.net_pay, cur)}</div>
            <div className="text-xs text-zinc-500 mt-1">Present {p.present_days}/{p.working_days} days · Gross {fmtMoney(p.gross, cur)}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">Net pay</Badge>
            <span className="text-[10px] text-zinc-400 flex items-center gap-1">Breakdown {open ? <CaretUp size={10} /> : <CaretDown size={10} />}</span>
          </div>
        </div>
      </button>
      {open && (
        <div className="pt-3 border-t border-zinc-100 text-xs font-mono-plex space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Earnings</div>
          <BreakRow label="Basic" value={fmtMoney(snap.basic, cur)} />
          <BreakRow label="HRA" value={fmtMoney(snap.hra, cur)} />
          <BreakRow label="Special allowance" value={fmtMoney(snap.special_allowance, cur)} />
          <BreakRow label="Conveyance" value={fmtMoney(snap.conveyance, cur)} />
          <BreakRow label="Medical" value={fmtMoney(snap.medical_allowance, cur)} />
          {Object.entries(customE).map(([k, v]) => <BreakRow key={k} label={k} value={fmtMoney(v, cur)} />)}
          <div className="border-t border-zinc-100 my-1" />
          <BreakRow label="Gross" value={fmtMoney(p.gross, cur)} bold />

          <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1 mt-2">Deductions</div>
          <BreakRow label="Employee PF" value={`− ${fmtMoney(p.deductions?.employee_pf, cur).replace(cur + " ", "")}`} muted />
          <BreakRow label="Employee ESIC" value={`− ${fmtMoney(p.deductions?.employee_esic, cur).replace(cur + " ", "")}`} muted />
          <BreakRow label="Professional Tax" value={`− ${fmtMoney(p.deductions?.professional_tax, cur).replace(cur + " ", "")}`} muted />
          <BreakRow label="TDS" value={`− ${fmtMoney(p.deductions?.tds, cur).replace(cur + " ", "")}`} muted />
          {Object.entries(customD).map(([k, v]) => <BreakRow key={k} label={k} value={`− ${fmtMoney(v, cur).replace(cur + " ", "")}`} muted />)}
          <div className="border-t border-zinc-100 my-1" />
          <BreakRow label="Net pay" value={fmtMoney(p.net_pay, cur)} bold />
        </div>
      )}
    </div>
  );
}

function BreakRow({ label, value, bold, muted }) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold text-zinc-900" : muted ? "text-zinc-500" : "text-zinc-700"}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

function KV({ label, value, muted, strong }) {
  return (
    <div className={`flex flex-col ${strong ? "font-semibold text-zinc-900" : muted ? "text-zinc-500" : "text-zinc-700"}`}>
      <span className="text-[10px] uppercase tracking-widest text-zinc-400">{label}</span>
      <span className="font-mono-plex">{value}</span>
    </div>
  );
}

function KVSmall({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400">{k}</div>
      <div className="font-mono-plex">{v}</div>
    </div>
  );
}
